import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { SubmitButton } from '../Buttons/SubmitButton';
import styles from './ImagePreviewModal.module.css';

interface ImagePreviewModalProps {
  src: string;
  alt?: string;
  onClose: () => void;
  /** Optional: render Use-in-prompt button under the image */
  onUse?: () => void;
  /** Optional: override default download behaviour (falls back to fetch+save from src) */
  onDownload?: () => void | Promise<void>;
  /** Set true to render Download button (default false — back-compat with chat) */
  showDownload?: boolean;
  /** Custom label for the Use button */
  useLabel?: string;
}

const ImagePreviewModal = ({
  src,
  alt,
  onClose,
  onUse,
  onDownload,
  showDownload,
  useLabel,
}: ImagePreviewModalProps) => {
  const { t } = useTranslation(['chat', 'image', 'common']);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prev;
    };
  }, [handleKeyDown]);

  const hasActions = !!(onUse || showDownload || onDownload);

  const handleDownload = useCallback(async () => {
    if (onDownload) {
      await onDownload();
      return;
    }
    try {
      const res = await fetch(src);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = alt || 'image';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // silent fail
    }
  }, [onDownload, src, alt]);

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      {/* Fallback close (chat usage) — only when no custom action bar */}
      {!hasActions && (
        <button className={styles.closeButton} onClick={onClose} aria-label={t('gallery.close')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}

      {/* Media column — image + action bar directly under it */}
      <div className={styles.mediaColumn} onClick={(e) => e.stopPropagation()}>
        <div className={styles.imageContainer}>
          <img
            src={src}
            alt={alt || 'Preview'}
            className={styles.image}
            draggable={false}
          />
        </div>

        {hasActions && (
          <div className={styles.actionBar}>
            <div className={styles.actionBarLeft}>
              <SubmitButton
                label={t('gallery.back', { defaultValue: 'Назад' })}
                variant="outline"
                direction="back"
                onClick={onClose}
              />
            </div>
            <div className={styles.actionBarRight}>
              {onUse && (
                <button
                  type="button"
                  className={styles.pillButton}
                  onClick={() => onUse()}
                >
                  <span className={styles.pillIcon} aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path fillRule="evenodd" d="M12 5a1 1 0 0 1 1 1v5h5a1 1 0 1 1 0 2h-5v5a1 1 0 1 1-2 0v-5H6a1 1 0 1 1 0-2h5V6a1 1 0 0 1 1-1Z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span>{useLabel || t('gallery.useAsReference', { defaultValue: 'Использовать' })}</span>
                </button>
              )}
              {(showDownload || onDownload) && (
                <button
                  type="button"
                  className={`${styles.pillButton} ${styles.pillPrimary}`}
                  onClick={handleDownload}
                >
                  <span className={styles.pillIcon} aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 3a1 1 0 0 1 1 1v8.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L11 12.586V4a1 1 0 0 1 1-1ZM5 17a1 1 0 1 1 2 0v1a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1a1 1 0 1 1 2 0v1a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3v-1Z" />
                    </svg>
                  </span>
                  <span>{t('btn.download', { ns: 'common', defaultValue: 'Скачать' })}</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* File name — only in chat-style (no actions) */}
      {alt && alt !== 'Preview' && alt !== 'Image' && !hasActions && (
        <div className={styles.fileName}>{alt}</div>
      )}
    </div>,
    document.body
  );
};

export default ImagePreviewModal;
